import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { getLenis, formatCurrency } from "@theme/utilities";
import { acquireThemeScrollLock, releaseThemeScrollLock } from "@theme/dialog";

class StickyAddToCart extends Component {
  requiredRefs = ["stickyBar", "panelContent", "summary", "addToCartButton", "addToCartSpinner", "addToCartText"];

  #abortController;
  #barController;
  #buyButtonsIntersectionObserver;
  #footerSectionObserver;
  #sizeObserver;
  #visibilityFrame;
  #isSticky = false;
  #expanded = false;
  #scrollLocked = false;
  #isAdding = false;
  #footerVisible = false;
  #mobile = window.matchMedia("(max-width: 767.98px)");

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest(".shopify-section");
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener("dialog:open", this.#onOtherDialog, { capture: true, signal });
    window.addEventListener("greeting-card:updated", this.#syncGreeting, { signal });
    this.#mobile.addEventListener("change", this.#refreshVisibility, { signal });
    window.addEventListener("scroll", this.#scheduleVisibilityRefresh, { passive: true, signal });
    window.addEventListener("resize", this.#scheduleVisibilityRefresh, { signal });
    this.#bindBar();
    this.#setupIntersectionObserver();
    this.#syncGreeting();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#visibilityFrame);
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#footerSectionObserver?.disconnect();
    this.#sizeObserver?.disconnect();
    this.#barController?.abort();
    this.#abortController?.abort();
    this.refs.stickyBar?.close();
    this.#releaseLock();
    this.#isSticky = false;
    this.#expanded = false;
    this.dataset.expanded = "false";
    this.refs.stickyBar.dataset.sticky = "false";
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindBar();
    this.querySelector('greeting-card-toggle')?.syncState();
    this.#syncGreeting();
    this.#setupIntersectionObserver();
  }

  #bindBar() {
    this.#barController?.abort();
    this.#sizeObserver?.disconnect();
    this.#barController = new AbortController();
    const { signal } = this.#barController;
    this.refs.stickyBar.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.collapse();
    }, { signal });
    this.refs.stickyBar.addEventListener("click", (event) => {
      if (!this.#expanded || event.target !== this.refs.stickyBar) return;
      const rect = this.refs.stickyBar.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        this.collapse();
      }
    }, { signal });
    this.#sizeObserver = new ResizeObserver(() => this.#sizeBar());
    this.#sizeObserver.observe(this.refs.panelContent);
  }

  #sizeBar() {
    this.refs.stickyBar.style.height = `${this.refs.panelContent.offsetHeight + 1}px`;
  }

  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#footerSectionObserver?.disconnect();
    const buyButtons = this.#getProductForm()?.closest(".buy-buttons-block");
    if (!buyButtons) return;
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#refreshVisibility);
    this.#buyButtonsIntersectionObserver.observe(buyButtons);
    const footer = document.querySelector("footer, .shopify-section-group-footer-group");
    if (footer) {
      this.#footerSectionObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = entry.isIntersecting;
        this.#refreshVisibility();
      }, { rootMargin: "0px 0px -200px 0px" });
      this.#footerSectionObserver.observe(footer);
    }
    this.#refreshVisibility();
  }

  #scheduleVisibilityRefresh = () => {
    if (this.#visibilityFrame) return;
    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = undefined;
      this.#refreshVisibility();
    });
  };

  #refreshVisibility = () => {
    const buyButtons = this.#getProductForm()?.closest(".buy-buttons-block");
    const visible = this.#mobile.matches && buyButtons && buyButtons.getBoundingClientRect().bottom <= 0 && !this.#footerVisible;
    if (visible) this.#show();
    else this.#hide();
  };

  #releaseLock() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    releaseThemeScrollLock();
  }

  #syncGreeting = () => {
    this.dataset.greetingActive = String(Boolean(window.greetingCardState?.active));
    this.#sizeBar();
  };

  expand = () => {
    if (!this.#isSticky || this.#expanded) return;
    const bar = this.refs.stickyBar;
    const keyboardFocus = this.refs.summary.matches(":focus-visible");
    const previousLayout = this.#captureLayout();
    bar.close();
    this.#expanded = true;
    this.dataset.expanded = "true";
    this.refs.summary.setAttribute("aria-expanded", "true");
    this.refs.summary.tabIndex = -1;
    this.#scrollLocked = true;
    acquireThemeScrollLock();
    bar.showModal();
    if (!keyboardFocus) bar.focus({ preventScroll: true });
    this.#sizeBar();
    this.#animateLayout(previousLayout);
  };

  collapse = (restoreFocus = true) => {
    if (!this.#expanded) return;
    const previousLayout = this.#captureLayout();
    this.refs.stickyBar.close();
    this.#expanded = false;
    this.dataset.expanded = "false";
    this.refs.summary.setAttribute("aria-expanded", "false");
    this.refs.summary.tabIndex = 0;
    this.#releaseLock();
    if (this.#isSticky && this.#mobile.matches) this.#showNonmodal();
    this.#sizeBar();
    this.#animateLayout(previousLayout);
    if (restoreFocus && this.#isSticky) this.refs.summary.focus({ preventScroll: true });
  };

  #captureLayout() {
    return [this.refs.addToCartButton, this.refs.stickyPrice].map(element => element.getBoundingClientRect());
  }

  #animateLayout(previous) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    [this.refs.addToCartButton, this.refs.stickyPrice].forEach((element, index) => {
      if (!element.animate) return;
      element.getAnimations().forEach(animation => animation.cancel());
      const rect = element.getBoundingClientRect();
      element.animate([
        { transform: `translate(${previous[index].left - rect.left}px, ${previous[index].top - rect.top}px)`, width: `${previous[index].width}px` },
        { transform: "none", width: `${rect.width}px` },
      ], { duration: 300, easing: "ease" });
    });
  }

  #onOtherDialog = (event) => {
    if (!this.contains(event.target)) this.collapse(false);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const html = event.detail.data?.html;

    if (!html) {
      this.dataset.currentVariantId = variant?.id || "";
      this.dataset.variantAvailable = String(Boolean(variant?.available));
      if (variant) {
        const price = formatCurrency(variant.price, window.FoxTheme?.moneyFormat || "${{amount}}");
        this.refs.stickyPrice.innerHTML = price;
        if (this.dataset.currencyCode) this.refs.stickyPrice.append(` ${this.dataset.currencyCode}`);
      }
      this.#updateButtonText(variant?.available, Boolean(variant && !variant.available));
      return;
    }

    const newStickyAddToCart = html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Morph only the panel: preserve the native dialog's top-layer/open state and scroll lock.
    const incomingPanel = newStickyBar.querySelector('[ref="panelContent"]');
    if (!incomingPanel) return;
    morph(this.refs.panelContent, incomingPanel);
    super.updatedCallback();
    this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    this.dataset.currentVariantId = variant?.id || "";
    this.querySelector('greeting-card-toggle')?.syncState();
    this.#syncGreeting();
    this.#bindBar();
    this.refs.summary.setAttribute("aria-expanded", String(this.#expanded));
    this.refs.summary.tabIndex = this.#expanded ? -1 : 0;

    const isSoldOut = variant && !variant.available && variant.inventory_management === "shopify";
    this.#updateButtonText(variant?.available, isSoldOut);

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
  };

  #handleVariantSelected = () => {
    // variant:selected carries an option-value ID; only variant:update confirms a merchandise ID.
    this.refs.addToCartButton.disabled = true;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    this.dataset.variantAvailable = "false";
    this.#updateButtonText(false);
  };

  /**
   * Viewport offset so the invalid control sits below the sticky header (not flush/covered).
   * Prefers `--header-height` from `BasicHeader` (#setHeight); falls back to
   * measuring `header[is="sticky-header"]` when the variable is unset or still 0 (e.g. first frame).
   *
   * @returns {number}
   */
  #getConstraintScrollClearancePx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--header-height").trim();
    let headerPx = parseFloat(raw);
    if (!Number.isFinite(headerPx) || headerPx <= 0) {
      const sticky = document.querySelector('header[is="sticky-header"]');
      headerPx = sticky instanceof HTMLElement ? Math.round(sticky.getBoundingClientRect().height) : 0;
    }

    const gapPx = 50;

    return headerPx + gapPx;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null}
   */
  #getFirstInvalidControl(form) {
    for (const el of form.elements) {
      if (
        !(el instanceof HTMLInputElement) &&
        !(el instanceof HTMLSelectElement) &&
        !(el instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (!el.willValidate || el.disabled) continue;
      if (!el.checkValidity()) return el;
    }
    return null;
  }

  /**
   * Browser `requestSubmit` scrolls the invalid field into view while Lenis smooth-scroll is active,
   * which fights native scroll and can jump the page (e.g. toward top) and dismiss the constraint UI.
   * We scroll via Lenis after `focus({ preventScroll: true })`, then call `reportValidity` only after
   * that scroll finishes (`onComplete` when `immediate: false`, or `scrollend` without Lenis).
   *
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | undefined} fallbackSubmitter
   */
  #reportInvalidFormConstraints(form, fallbackSubmitter) {
    const invalidEl = this.#getFirstInvalidControl(form);
    if (!invalidEl) {
      if (fallbackSubmitter?.type === "submit") {
        form.requestSubmit(fallbackSubmitter);
      } else {
        form.requestSubmit();
      }
      return;
    }

    const clearancePx = this.#getConstraintScrollClearancePx();

    invalidEl.focus({ preventScroll: true });

    const showBubble = () => {
      requestAnimationFrame(() => {
        invalidEl.reportValidity();
      });
    };

    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(invalidEl, {
        offset: -clearancePx,
        immediate: false,
        onComplete: showBubble,
      });
      return;
    }

    const rect = invalidEl.getBoundingClientRect();
    const targetTop = rect.top + window.scrollY - clearancePx;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });

    let settled = false;
    const finishAfterScroll = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer !== 0) {
        window.clearTimeout(fallbackTimer);
      }
      showBubble();
    };

    let fallbackTimer = 0;
    if ("onscrollend" in window) {
      window.addEventListener("scrollend", finishAfterScroll, { once: true, passive: true });
      fallbackTimer = window.setTimeout(finishAfterScroll, 1200);
    } else {
      fallbackTimer = window.setTimeout(finishAfterScroll, 450);
    }
  }

  handleAddToCartClick = () => {
    if (this.#isAdding || this.refs.addToCartButton.disabled) return;
    const productForm = this.#getProductForm();
    const picker = this.closest(".shopify-section")?.querySelector('variant-picker.is-variant-fetching');
    if (picker) return;
    const form = productForm?.querySelector("form");
    const targetButton = productForm?.querySelector('[ref="addToCartButton"]');
    const submitter =
      targetButton instanceof HTMLButtonElement && targetButton.type === "submit" ? targetButton : undefined;

    if (form && !form.checkValidity()) {
      this.collapse(false);
      this.#reportInvalidFormConstraints(form, submitter);
      return;
    }

    if (!targetButton || targetButton.disabled) return;
    this.collapse(false);
    this.#isAdding = true;
    this.#addLoading();
    targetButton.click();
  };

  #handleCartAddComplete = () => {
    this.#isAdding = false;
    this.#removeLoading();
  };

  #updateButtonText = (isAvailable, isSoldOut = false) => {
    const { addToCartText, addToCartButton } = this.refs;
    if (!addToCartText) return;
    addToCartButton.disabled = !isAvailable || this.#isAdding;

    if (isAvailable) {
      addToCartText.textContent = addToCartButton.dataset.addToCartText;
    } else if (isSoldOut) {
      addToCartText.textContent = addToCartButton.dataset.soldOutText;
    } else {
      addToCartText.textContent = addToCartButton.dataset.unavailableText;
    }
  };

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #showNonmodal() {
    const activeElement = document.activeElement;
    this.refs.stickyBar.show();
    if (activeElement instanceof HTMLElement && activeElement.isConnected && !this.contains(activeElement)) {
      const tabindex = activeElement.getAttribute("tabindex");
      if (activeElement === document.body) activeElement.tabIndex = -1;
      activeElement.focus({ preventScroll: true });
      if (activeElement === document.body) {
        if (tabindex === null) activeElement.removeAttribute("tabindex");
        else activeElement.setAttribute("tabindex", tabindex);
      }
    }
  }

  #show() {
    if (this.#isSticky) return;
    this.#isSticky = true;
    this.refs.stickyBar.dataset.sticky = "true";
    this.#showNonmodal();
    this.querySelector('greeting-card-toggle')?.syncState();
    this.#syncGreeting();
  }

  #hide() {
    if (!this.#isSticky && !this.#expanded) return;
    this.#isSticky = false;
    this.collapse(false);
    this.refs.stickyBar.close();
    this.refs.stickyBar.dataset.sticky = "false";
  }

  #addLoading() {
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.setAttribute("aria-busy", "true");
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner.classList.remove("hidden");
  }

  #removeLoading() {
    this.refs.addToCartButton.disabled = this.dataset.variantAvailable !== "true";
    this.refs.addToCartButton.removeAttribute("aria-busy");
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner.classList.add("hidden");
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCart);
}
